/**
 * shopRoutes.js, single source of truth for the shop's department + category
 * architecture.
 *
 * URL structure (product URLs stay FLAT and stable, never nested):
 *   /shop                       → department landing (Women / Men tiles + featured)
 *   /shop/women                 → gender = female
 *   /shop/women/dresses         → gender = female AND category = Clothing
 *   /shop/men                   → gender = male
 *   /shop/men/shoes             → gender = male AND category = Shoes
 *   /shop/brand/:slug           → brand_slug match
 *   /shop/retailer/:slug        → retailer_slug match
 *   /shop/:product-slug         → product detail (anything not matching the above)
 *
 * Adding a department or category = ONE entry here. Routing (main.jsx), the
 * tab bar, query filters, URL generation and the router's reserved segments all
 * derive from this file.
 *
 * RULES (enforced by applyShopFilter + validateShopRoutes):
 *   1. Departments filter by exact `.eq('gender', dept.gender)` (lowercase, as
 *      stored in the DB: 'female' / 'male' / 'kids').
 *   2. type:'category' categories filter by exact `.eq('category', cat.category)`.
 *   3. type:'occasion' categories filter by `.overlaps('occasion_tags', cat.tags)`
 *      and need a partial index (idx_products_occ_<key>) before launch.
 *   4. NO route filter may use ilike.
 *   5. A category is shown under a department only if the department id is in its
 *      `departments` array. Disabled departments (enabled:false) are hidden from
 *      the tab bar AND not routable (their URL falls through to a product slug).
 *   6. category values are case-sensitive and must match products.category exactly.
 */

export const DEFAULT_SIZES = ['XS','S','M','L','XL','4','6','8','10','12','14','16','18'];

/**
 * LOCKED occasion vocabulary, the SINGLE source of truth for the whole platform.
 * Drives: occasion_tags DB values, partial indexes, the Wedding world nav, the
 * importer's deterministic seeding, and the AI occasion-classification prompt
 * boundary. NOTHING may write an occasion tag outside this list. Do not add
 * synonyms (no 'bride'/'bridal-gown' alongside 'bridal'), exactly one token each.
 */
export const CONTROLLED_OCCASION_VOCAB = [
  'bridal',
  'bridesmaid',
  'wedding-guest',
  'mother-of-bride',
  'mother-of-groom',
  'groom',
  'groomsmen',
  'engagement',
  'proposal',
  'reception',
  'after-party',
  'honeymoon',
  'registry',
  'luxury-gift',
];
export const isOccasionTag = (t) => CONTROLLED_OCCASION_VOCAB.includes(t);

/**
 * Home & Living detector. Feeds (esp. NET-A-PORTER) file homeware/lifestyle
 * goods under fashion categories; these signals (mostly from the subcategory)
 * reliably identify them so they land in their own world, not in Clothing.
 * Tested against subcategory + name.
 */
export const HOME_LIVING_RE = /table ?linen|tablecloth|napkin|glassware|tumbler|decanter|carafe|stemware|\bvase\b|candle|candelabra|candlestick|serveware|servingware|cutlery|flatware|dinnerware|tableware|cups? and mugs?|\bmug\b|home accessor|homeware|home ?decor|games? and leisure|backgammon|\bchess\b|checkers|\bpuzzle\b|cushion|throw blanket|bedding|duvet|placemat|coaster|picture frame|book cover|notebook|stationery|ornament|incense|room spray|trinket/i;

/**
 * LOCKED clothing subtype vocabulary, the drill level under Women/Men > Clothing
 * (e.g. /shop/women/clothing/pants). Auto-assigned by normalizeSubcategory.
 */
export const CLOTHING_SUBTYPES = [
  'dresses', 'tops', 'pants', 'skirts', 'knitwear', 'outerwear',
  'jumpsuits', 'swimwear', 'lingerie', 'activewear', 'other',
];

/**
 * Deterministically classify a product into a clean subtype from its (normalized)
 * category + raw subcategory + name. Pattern-based so unseen feed values AND null
 * subcategories (inferred from the name) auto-classify. Order matters: the most
 * specific buckets (activewear, swimwear, lingerie) are tested first so e.g. a
 * "tennis dress" → activewear (not dresses) and a "bodysuit" → lingerie (not tops).
 * AI (Layer 2) can refine edge cases later.
 */
export function normalizeSubcategory(category, subcategory, name = '') {
  const t = `${subcategory || ''} ${name || ''}`.toLowerCase();
  const has = (re) => re.test(t);

  switch (category) {
    case 'Clothing':
      if (has(/tennis|yoga|gym|sport|active|workout|running|track\b|bike short|cycling|legging|base ?layer/)) return 'activewear';
      if (has(/swim|bikini|one.?piece|beachwear|sarong|cover.?up/)) return 'swimwear';
      if (has(/lingerie|\bbras?\b|bralette|brief|knicker|thong|intimate|shapewear|bodysuit|corset|\brobe\b|sleepwear|nightwear|nightie|chemise|negligee|pyjama|pajama|loungewear|tights|hosiery|stocking/)) return 'lingerie';
      if (has(/dress|gown|kaftan|caftan/)) return 'dresses';
      if (has(/skirt/)) return 'skirts';
      if (has(/jumpsuit|playsuit|romper|dungaree/)) return 'jumpsuits';
      if (has(/blazer|jacket|coat|trench|parka|gilet|\bcape\b|outerwear|puffer|anorak/)) return 'outerwear';
      if (has(/knit|sweater|cardigan|cashmere|jumper|pullover|turtleneck|sweatshirt|hoodie/)) return 'knitwear';
      if (has(/pant|trouser|jean|denim|culotte|\bleg\b|short|chino|jogger/)) return 'pants';
      if (has(/top|blouse|shirt|cami|tank|\btee\b|t-shirt|vest|camisole|bustier/)) return 'tops';
      return 'other';
    case 'Shoes':
      if (has(/sneaker|trainer|low.?top|high.?top/)) return 'sneakers';
      if (has(/boot/)) return 'boots';
      if (has(/sandal|slide|flip.?flop|thong/)) return 'sandals';
      if (has(/mule|slingback/)) return 'mules';
      if (has(/ballet|loafer|espadrille|moccasin|\bflat/)) return 'flats';
      if (has(/pump|court/)) return 'pumps';
      if (has(/heel|stiletto|wedge/)) return 'heels';
      if (has(/slipper/)) return 'slippers';
      return 'other';
    case 'Bags':
      if (has(/clutch|evening bag/)) return 'clutches';
      if (has(/tote/)) return 'totes';
      if (has(/cross.?body/)) return 'crossbody';
      if (has(/backpack/)) return 'backpacks';
      if (has(/wallet|card.?holder|pouch|purse|coin/)) return 'small-leather-goods';
      if (has(/shoulder|bag|hobo|bucket|satchel|top.?handle/)) return 'shoulder';
      return 'other';
    case 'Jewelry And Watches':
      if (has(/watch/)) return 'watches';
      if (has(/necklace|pendant|choker/)) return 'necklaces';
      if (has(/earring|stud|hoop/)) return 'earrings';
      if (has(/\bring\b/)) return 'rings';
      if (has(/bracelet|bangle|cuff|anklet/)) return 'bracelets';
      return 'other';
    case 'Accessories':
      if (has(/sunglass|eyewear|glasses/)) return 'sunglasses';
      if (has(/belt/)) return 'belts';
      if (has(/\bhat\b|cap\b|beanie|fedora|beret/)) return 'hats';
      if (has(/scarf|shawl|wrap|stole/)) return 'scarves';
      if (has(/glove/)) return 'gloves';
      if (has(/hair|clip|headband|barrette|scrunchie/)) return 'hair';
      if (has(/sock|tights|hosiery|stocking/)) return 'hosiery';
      return 'other';
    case 'Home & Living':
      if (has(/glassware|tumbler|decanter|carafe|stemware|wine glass/)) return 'glassware';
      if (has(/table ?linen|tablecloth|napkin|placemat|coaster|dinnerware|serveware|cutlery|flatware|plate|bowl|tableware|cups? and mugs?|\bmug\b/)) return 'tableware';
      if (has(/candle|candelabra|candlestick|incense|room spray|diffuser/)) return 'candles-scent';
      if (has(/games? and leisure|backgammon|chess|checkers|puzzle/)) return 'games';
      if (has(/cushion|throw|bedding|duvet|blanket/)) return 'soft-furnishings';
      if (has(/vase|ornament|picture frame|decor|trinket|sculpture/)) return 'decor';
      return 'other';
    case 'Beauty':
      if (has(/fragrance|perfume|cologne|eau de|parfum|scent/)) return 'fragrance';
      if (has(/serum|cream|moisturis|cleanser|skin|toner|mask|spf/)) return 'skincare';
      if (has(/lipstick|mascara|foundation|makeup|blush|concealer|palette/)) return 'makeup';
      if (has(/shampoo|conditioner|\bhair/)) return 'haircare';
      if (has(/candle|diffuser|home|fragranc/)) return 'home';
      return 'other';
    default:
      return 'other';
  }
}

const titleCase = (s) => s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// ── Taxonomy (single source of truth) ───────────────────────────────────────
// DEPARTMENTS / CATEGORIES / WEDDING_SECTIONS / shopPath / subtypeLabel live in
// supabase/functions/_shared/shopTaxonomy.js so the Aura orchestrator reads the SAME
// definition this router does (an edge function cannot import from src/). Re-exported
// here so every existing client import keeps working unchanged.
export {
  DEPARTMENTS, CATEGORIES, WEDDING_SECTIONS, shopPath, subtypeLabel,
} from '../../supabase/functions/_shared/shopTaxonomy.js';
// NOTE: `export ... from` re-exports WITHOUT creating a local binding, so every
// name this file's own helpers call must also be imported. Omitting subtypeLabel
// and shopPath silently broke getSubtypesForCategory() — and with it the shop's
// category navigation (caught 2026-07-30).
import {
  DEPARTMENTS, CATEGORIES, WEDDING_SECTIONS, subtypeLabel, shopPath,
} from '../../supabase/functions/_shared/shopTaxonomy.js';

// Whole-world default: any wedding-relevant occasion.
export const WEDDING_OCCASION_SET = [...CONTROLLED_OCCASION_VOCAB];

// ── Derived slugs (router validation) ───────────────────────────────────────
export const DEPARTMENT_SLUGS = Object.entries(DEPARTMENTS).filter(([, d]) => d.enabled).map(([id]) => id);
// Second-level segment can be a category (gender depts) OR a wedding section.
export const SECOND_LEVEL_SLUGS = [...Object.keys(CATEGORIES), ...Object.keys(WEDDING_SECTIONS)];
// Third-level segment is a subtype token (any category's subtypes).
export const SUBTYPE_SLUGS = [...new Set(Object.values(CATEGORIES).flatMap(c => c.subtypes || []))];
// Back-compat alias (older imports).
export const CATEGORY_SLUGS = SECOND_LEVEL_SLUGS;

export function getDepartment(id) { return id ? DEPARTMENTS[id] : null; }
export function getCategory(id)   { return id ? CATEGORIES[id]   : null; }
export function isDepartment(id)  { return !!(id && DEPARTMENTS[id] && DEPARTMENTS[id].enabled); }
export function isCategory(id)    { return !!(id && CATEGORIES[id]); }
export function isWeddingSection(id) { return !!(id && WEDDING_SECTIONS[id]); }
export function isSubtype(id)     { return SUBTYPE_SLUGS.includes(id); }

/** Enabled departments in display order: [{ id, label, type, gender }]. */
export function getEnabledDepartments() {
  return Object.entries(DEPARTMENTS).filter(([, d]) => d.enabled).map(([id, d]) => ({ id, ...d }));
}

/** Second-level tabs for a department: categories (gender) or wedding sections. */
export function getSectionsForDepartment(departmentId) {
  const dept = DEPARTMENTS[departmentId];
  if (!dept) return [];
  if (dept.type === 'occasion') return Object.entries(WEDDING_SECTIONS).map(([id, s]) => ({ id, label: s.label }));
  return Object.entries(CATEGORIES)
    .filter(([, c]) => c.departments.includes(departmentId))
    .map(([id, c]) => ({ id, label: c.label }));
}
// Alias kept for existing callers.
export const getCategoriesForDepartment = getSectionsForDepartment;

/** Subtype tabs under a category: [{ id, label }] (gender depts only). */
export function getSubtypesForCategory(categoryId) {
  return (CATEGORIES[categoryId]?.subtypes || []).map(id => ({ id, label: subtypeLabel(id) }));
}

export function getRouteSizes(categoryId) { return CATEGORIES[categoryId]?.sizes || DEFAULT_SIZES; }

export function getDepartmentLabel(id) { return DEPARTMENTS[id]?.label || (id ? titleCase(id) : ''); }
export function getSecondLevelLabel(id) { return CATEGORIES[id]?.label || WEDDING_SECTIONS[id]?.label || (id ? titleCase(id) : ''); }
export function getSubtypeLabel(id) { return id ? subtypeLabel(id) : ''; }

/** Build a shop URL from a view. */
/**
 * Apply a view's filters to a Supabase query builder. Never ilike.
 * Gender world:   gender eq  (+ category eq, + subtype eq)
 * Wedding world:  occasion overlaps (+ category eq for combo sections)
 */
export function applyShopFilter(query, { department, category, subtype } = {}) {
  let q = query;
  const dept = DEPARTMENTS[department];
  if (!dept) return q;
  if (dept.type === 'occasion') {
    const section = category && WEDDING_SECTIONS[category];
    if (section) {
      q = q.overlaps('occasion_tags', section.tags);
      if (section.category) q = q.eq('category', section.category);
    } else {
      q = q.overlaps('occasion_tags', WEDDING_OCCASION_SET);
    }
    return q;
  }
  // gender world
  q = q.eq('gender', dept.gender);
  const cat = category && CATEGORIES[category];
  if (cat) q = q.eq('category', cat.category);
  if (subtype) q = q.eq('subtype', subtype);
  return q;
}

/**
 * Product detail breadcrumb target: map a product to its nested view.
 * Returns { department, category, subtype } (keys may be null).
 */
export function productView(gender, categoryValue, subtype) {
  const g = (gender || '').toLowerCase();
  const department = g === 'male' ? 'men' : g === 'kids' ? 'kids' : 'women';
  let categoryKey = null;
  if (categoryValue) {
    const cv = categoryValue.toLowerCase();
    for (const [id, c] of Object.entries(CATEGORIES)) {
      if (c.category && c.category.toLowerCase() === cv && c.departments.includes(department)) { categoryKey = id; break; }
    }
  }
  const sub = (categoryKey && subtype && CATEGORIES[categoryKey].subtypes.includes(subtype)) ? subtype : null;
  return { department: isDepartment(department) ? department : null, category: categoryKey, subtype: sub };
}
// Back-compat: older breadcrumb code expects { department, categoryKey }.
export function productDepartmentCategory(gender, categoryValue) {
  const v = productView(gender, categoryValue, null);
  return { department: v.department, categoryKey: v.category };
}

/** Dev-only shape check. */
export function validateShopRoutes() {
  for (const [id, c] of Object.entries(CATEGORIES)) {
    if (!c.label) console.error(`[shopRoutes] category "${id}" missing label`);
    if (!c.category) console.error(`[shopRoutes] category "${id}" missing exact category value`);
    if (!Array.isArray(c.departments) || !c.departments.length) console.error(`[shopRoutes] category "${id}" missing departments[]`);
  }
  for (const [id, s] of Object.entries(WEDDING_SECTIONS)) {
    if (!Array.isArray(s.tags) || !s.tags.length) console.error(`[shopRoutes] wedding section "${id}" missing tags`);
    s.tags.forEach(t => { if (!CONTROLLED_OCCASION_VOCAB.includes(t)) console.error(`[shopRoutes] wedding section "${id}" uses off-vocab tag "${t}"`); });
  }
}
